import { useFormat, useLocale } from '../../hooks/index';
import { AppFormattedMessage } from '../app-formatted-message/index';

const getMultiplePrefix = ({
  isMultiple,
  singularStr,
  pLuralStr,
  time,
}: {
  isMultiple: boolean;
  pLuralStr?: string;
  singularStr: string;
  time?: number | string;
}) => {
  return isMultiple ? `${time || 1} ${pLuralStr}` : `${time} ${singularStr}`;
};

export const FormattedDateDistance = ({ date }: { date?: Date | null }) => {
  const { format } = useFormat();
  const { messagesLocale } = useLocale();

  if (!date) return '-';

  const diffInMs = new Date().getTime() - (date.getTime() - 100);
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);

  if (diffInSeconds < 60) return <AppFormattedMessage id="LESS_THAN_MIN_AGO" />;

  if (diffInMinutes < 60)
    return (
      <AppFormattedMessage
        id="MINUTES_AGO"
        values={{
          date: getMultiplePrefix({
            isMultiple: diffInMinutes >= 2,
            singularStr: messagesLocale['SINGULAR_MIN'],
            pLuralStr: messagesLocale['PLURAL_MIN'],
            time: diffInMinutes,
          }),
        }}
      />
    );

  if (diffInHours < 24)
    return (
      <AppFormattedMessage
        id="HOURS_AGO"
        values={{
          date: getMultiplePrefix({
            isMultiple: diffInHours >= 2,
            singularStr: messagesLocale['SINGULAR_HOUR'],
            pLuralStr: messagesLocale['PLURAL_HOUR'],
            time: diffInHours,
          }),
        }}
      />
    );

  if (diffInDays < 30)
    return (
      <AppFormattedMessage
        id="DAYS_AGO"
        values={{
          date: getMultiplePrefix({
            isMultiple: diffInDays >= 2,
            singularStr: messagesLocale['SINGULAR_DAY'],
            pLuralStr: messagesLocale['PLURAL_DAY'],
            time: diffInDays,
          }),
        }}
      />
    );

  if (diffInDays < 365)
    return (
      <AppFormattedMessage
        id="MONTHS_AGO"
        values={{
          date: getMultiplePrefix({
            isMultiple: diffInDays >= 2,
            singularStr: messagesLocale['SINGULAR_MONTH'],
            pLuralStr: messagesLocale['PLURAL_MONTH'],
            time: diffInMonths,
          }),
        }}
      />
    );

  return format(date, 'dd/MM/yyy');
};
